import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useFeedback } from '../../contexts/FeedbackContext';
import { useAuth } from '../../context/AuthContext';
import { doc, getDoc, updateDoc, collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { getProfilePlaceholder } from '../../utils/placeholders';
import notificationService from '../../services/notificationService';
import {
  CheckCircle,
  XCircle,
  Clock,
  Image,
  Play,
  FileText,
  Hash,
  AtSign,
  Calendar,
  User,
  AlertTriangle
} from 'lucide-react';
import LoadingSpinner from '../../components/LoadingSpinner';
import Breadcrumb from '../../components/common/Breadcrumb';

const VideoPlayer = ({ src }) => {
  const videoRef = React.useRef(null);
  const [isPlaying, setIsPlaying] = React.useState(false);

  const handlePlayClick = () => {
    if (videoRef.current) {
      videoRef.current.play();
    }
  };

  const handleFullscreen = (e) => {
    e.stopPropagation();
    if (videoRef.current) {
      if (videoRef.current.requestFullscreen) {
        videoRef.current.requestFullscreen();
      } else if (videoRef.current.webkitRequestFullscreen) {
        videoRef.current.webkitRequestFullscreen();
      } else if (videoRef.current.webkitEnterFullscreen) {
        videoRef.current.webkitEnterFullscreen();
      }
    }
  };

  return (
    <div className="relative group rounded-lg overflow-hidden bg-black">
      <video
        ref={videoRef}
        src={src}
        className="w-full rounded-lg"
        controls
        preload="metadata"
        onPlay={() => setIsPlaying(true)}
        onPause={() => setIsPlaying(false)}
        onEnded={() => setIsPlaying(false)}
      />
      {/* Play overlay — visible only when paused */}
      {!isPlaying && (
        <div
          className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-30 cursor-pointer transition-opacity duration-200"
          onClick={handlePlayClick}
        >
          <div className="w-16 h-16 rounded-full bg-white bg-opacity-90 flex items-center justify-center shadow-lg hover:scale-110 transition-transform duration-200">
            <Play className="h-8 w-8 text-gray-900 ml-1" />
          </div>
        </div>
      )}
      {/* Fullscreen button */}
      <button
        onClick={handleFullscreen}
        className="absolute top-2 right-2 p-1.5 rounded-md bg-black bg-opacity-50 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-200 hover:bg-opacity-70"
        title="Fullscreen"
      >
        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M4 8V4m0 0h4M4 4l5 5m11-5h-4m4 0v4m0 0l-5-5m-7 14H4m0 0v-4m0 4l5-5m11 5h-4m4 0v-4m0 4l-5-5" />
        </svg>
      </button>
    </div>
  );
};

const ContentPreApprovalDetail = () => {
  const { id: draftId } = useParams();
  const navigate = useNavigate();
  const { showSuccess, showError, handleAsyncOperation } = useFeedback();
  const { currentUser } = useAuth();
  
  const [draft, setDraft] = useState(null);
  const [campaign, setCampaign] = useState(null);
  const [influencer, setInfluencer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [feedbackText, setFeedbackText] = useState('');
  const [showApprovalModal, setShowApprovalModal] = useState(false);
  const [showRejectionModal, setShowRejectionModal] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const fetchDraftDetails = async () => {
      if (!currentUser || !draftId) {
        setLoading(false);
        return;
      }
      
      try {
        setLoading(true);
        
        // Fetch draft document
        const draftRef = doc(db, 'contentDrafts', draftId);
        const draftSnap = await getDoc(draftRef);
        
        if (!draftSnap.exists()) {
          showError('Content draft not found.', 'The draft may have been deleted or you may not have permission to view it.', 6000);
          navigate('/brand/content-pre-approval');
          return;
        }
        
        const draftData = draftSnap.data();
        
        // Verify brand ownership
        if (draftData.brandId !== currentUser.uid) {
          showError('Access denied.', 'You do not have permission to view this content draft.', 6000);
          navigate('/brand/content-pre-approval');
          return;
        }
        
        setDraft({ id: draftSnap.id, ...draftData });
        
        // Fetch campaign details
        if (draftData.campaignId) {
          const campaignRef = doc(db, 'campaigns', draftData.campaignId);
          const campaignSnap = await getDoc(campaignRef);
          if (campaignSnap.exists()) {
            setCampaign({ id: campaignSnap.id, ...campaignSnap.data() });
          }
        }
        
        // Fetch influencer details
        if (draftData.influencerId) {
          const influencerRef = doc(db, 'users', draftData.influencerId);
          const influencerSnap = await getDoc(influencerRef);
          if (influencerSnap.exists()) {
            setInfluencer({ id: influencerSnap.id, ...influencerSnap.data() });
          }
        }
        
      } catch (error) {
        console.error('Error fetching draft details:', error);
        showError('Failed to load content draft details.', 'Please refresh the page and try again.', 6000);
      } finally {
        setLoading(false);
      }
    };

    fetchDraftDetails();
  }, [currentUser, draftId, showError, navigate]);

  const handleApproveDraft = async () => {
    if (!draft) return;
    
    try {
      setSubmitting(true);
      
      await handleAsyncOperation(async () => {
        // Update draft status
        const draftRef = doc(db, 'contentDrafts', draft.id);
        await updateDoc(draftRef, {
          status: 'approved',
          brandFeedback: feedbackText || 'Approved for posting',
          approvedAt: new Date()
        });

        // Update campaign participant status
        const participantsRef = collection(db, 'campaignParticipants');
        const q = query(
          participantsRef,
          where('campaignId', '==', draft.campaignId),
          where('influencerId', '==', draft.influencerId)
        );
        
        const querySnapshot = await getDocs(q);
        if (!querySnapshot.empty) {
          const participantId = querySnapshot.docs[0].id;
          const participantRef = doc(db, 'campaignParticipants', participantId);
          await updateDoc(participantRef, {
            draftStatus: 'approved',
            draftApprovedAt: new Date()
          });
        }

        // Send notification to influencer
        await notificationService.createNotification(
          draft.influencerId,
          'draft_approved',
          'Content Draft Approved!',
          `Your content draft for "${campaign?.name || 'the campaign'}" has been approved. You can now post your content and submit final proof.`,
          {
            relatedId: draft.id,
            relatedType: 'contentDraft',
            campaignId: draft.campaignId
          }
        );

        // Update local state
        setDraft(prev => ({ 
          ...prev, 
          status: 'approved', 
          brandFeedback: feedbackText || 'Approved for posting',
          approvedAt: new Date()
        }));
      });

      showSuccess('Content draft approved successfully!', 'The influencer has been notified and can now post their content.', 4000);
      setShowApprovalModal(false);
      setFeedbackText('');
    } catch (error) {
      console.error('Error approving draft:', error);
      showError('Failed to approve content draft.', 'Please try again.', 5000);
    } finally {
      setSubmitting(false);
    }
  };

  const handleRejectDraft = async () => {
    if (!draft || !feedbackText.trim()) {
      showError('Feedback is required when requesting revision.', 'Please provide specific feedback for the influencer.', 5000);
      return;
    }
    
    try {
      setSubmitting(true);
      
      await handleAsyncOperation(async () => {
        // Update draft status
        const draftRef = doc(db, 'contentDrafts', draft.id);
        await updateDoc(draftRef, {
          status: 'rejected',
          brandFeedback: feedbackText,
          rejectedAt: new Date()
        });

        // Update campaign participant status
        const participantsRef = collection(db, 'campaignParticipants');
        const q = query(
          participantsRef,
          where('campaignId', '==', draft.campaignId),
          where('influencerId', '==', draft.influencerId)
        );
        
        const querySnapshot = await getDocs(q);
        if (!querySnapshot.empty) {
          const participantId = querySnapshot.docs[0].id;
          const participantRef = doc(db, 'campaignParticipants', participantId);
          await updateDoc(participantRef, {
            draftStatus: 'revision_requested',
            draftFeedback: feedbackText,
            draftRevisionRequestedAt: new Date()
          });
        }

        // Send notification to influencer
        await notificationService.createNotification(
          draft.influencerId,
          'draft_rejected',
          'Content Draft Needs Revision',
          `Your content draft for "${campaign?.name || 'the campaign'}" needs revision. Please check the feedback and resubmit.`,
          {
            relatedId: draft.id,
            relatedType: 'contentDraft',
            campaignId: draft.campaignId
          }
        );

        // Update local state
        setDraft(prev => ({ 
          ...prev, 
          status: 'rejected', 
          brandFeedback: feedbackText,
          rejectedAt: new Date()
        }));
      });

      showSuccess('Revision requested successfully!', 'The influencer has been notified and can resubmit their draft.', 4000);
      setShowRejectionModal(false);
      setFeedbackText('');
    } catch (error) {
      console.error('Error rejecting draft:', error);
      showError('Failed to request revision.', 'Please try again.', 5000);
    } finally {
      setSubmitting(false);
    }
  };

  const getStatusBadge = (status) => {
    const baseClasses = "inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium";
    
    switch (status) {
      case 'pending':
        return (
          <span className={`${baseClasses} bg-yellow-100 text-yellow-800`}>
            <Clock className="h-3 w-3 mr-1" />
            Pending Review
          </span>
        );
      case 'approved':
        return (
          <span className={`${baseClasses} bg-green-100 text-green-800`}>
            <CheckCircle className="h-3 w-3 mr-1" />
            Approved
          </span>
        );
      case 'rejected':
        return (
          <span className={`${baseClasses} bg-red-100 text-red-800`}>
            <XCircle className="h-3 w-3 mr-1" />
            Revision Requested
          </span>
        );
      default:
        return (
          <span className={`${baseClasses} bg-gray-100 text-gray-800`}>
            {status || 'Unknown'}
          </span>
        );
    }
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    
    try {
      const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (error) {
      console.error('Error formatting date:', error);
      return 'Invalid date';
    }
  };

  const renderMediaPreview = (file) => {
    if (!file || !file.url) return null;

    const isVideo = file.type?.startsWith('video/') || file.url.includes('.mp4') || file.url.includes('.mov');
    
    return (
      <div className="relative group">
        {isVideo ? (
          <VideoPlayer src={file.url} />
        ) : (
          <img
            src={file.url}
            alt="Content preview"
            className="w-full h-48 object-cover rounded-lg"
            onError={(e) => {
              e.target.style.display = 'none';
            }}
          />
        )}
        <div className="mt-2 text-xs text-gray-500 truncate">
          {file.name || 'Media file'}
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner />
      </div>
    );
  }

  if (!draft) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <AlertTriangle className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">Content Draft Not Found</h3>
          <p className="text-gray-600 mb-4">The content draft you're looking for doesn't exist or has been removed.</p>
          <Breadcrumb
            items={[
              { label: 'Content Pre-Approval', to: '/brand/content-pre-approval' },
              { label: 'Content Pre-Approval Detail' },
            ]}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="pb-12">
      {/* Header */}
      <div className="mb-6">
        <div className="mb-4">
          <Breadcrumb
            items={[
              { label: 'Content Pre-Approval', to: '/brand/content-pre-approval' },
              { label: 'Content Pre-Approval Detail' },
            ]}
          />
        </div>
        
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-semibold text-gray-800 mb-2">Content Draft Review</h2>
            <p className="text-gray-600">Review and approve content before it goes live</p>
          </div>
          <div className="flex items-center space-x-3">
            {getStatusBadge(draft.status)}
            {draft.resubmittedAt && (
              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
                Resubmission
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 xl:grid-cols-4 gap-8">
        {/* Left Column - Main Content */}
        <div className="xl:col-span-3 space-y-8">
          {/* Campaign & Influencer Info Row */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Campaign Info Card */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
              <div className="flex items-center mb-4">
                <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center mr-3">
                  <FileText className="h-5 w-5 text-blue-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900">Campaign Details</h3>
              </div>
              
              <div className="space-y-3">
                <div>
                  <p className="text-sm font-medium text-gray-700 mb-1">Campaign Name</p>
                  <p className="text-base text-gray-900 font-medium">{campaign?.name || 'Unknown Campaign'}</p>
                </div>
                
                {campaign?.deadline && (
                  <div>
                    <p className="text-sm font-medium text-gray-700 mb-1">Deadline</p>
                    <div className="flex items-center text-sm text-gray-600">
                      <Calendar className="h-4 w-4 mr-2 text-gray-400" />
                      {formatDate(campaign.deadline)}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Influencer Info Card */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
              <div className="flex items-center mb-4">
                <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center mr-3">
                  <User className="h-5 w-5 text-green-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900">Influencer</h3>
              </div>
              
              <div className="flex items-center space-x-4">
                <img
                  className="h-12 w-12 rounded-full object-cover ring-2 ring-gray-100"
                  src={influencer?.profileImageUrl || influencer?.photoURL || getProfilePlaceholder()}
                  alt={influencer?.displayName || 'Influencer'}
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.src = getProfilePlaceholder();
                  }}
                />
                <div>
                  <p className="text-base font-semibold text-gray-900">
                    {influencer?.displayName || 'Unknown Influencer'}
                  </p>
                  <p className="text-sm text-gray-600">{influencer?.email}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Content Files Section */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
            <div className="flex items-center mb-6">
              <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center mr-3">
                <Image className="h-5 w-5 text-purple-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900">Content Files</h3>
            </div>
            
            {draft.contentUrls && draft.contentUrls.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {draft.contentUrls.map((url, index) => (
                  <div key={index} className="relative group">
                    <div className="aspect-square rounded-lg overflow-hidden bg-gray-50 border border-gray-200">
                      {renderMediaPreview({ url, type: url.includes('.mp4') || url.includes('.mov') ? 'video/mp4' : 'image/jpeg' })}
                    </div>
                    <p className="text-xs text-gray-500 mt-2 text-center">Media file {index + 1}</p>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12 border-2 border-dashed border-gray-200 rounded-lg">
                <Image className="h-16 w-16 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-500 font-medium">No content files uploaded</p>
                <p className="text-sm text-gray-400 mt-1">Files will appear here once uploaded by the influencer</p>
              </div>
            )}
          </div>

          {/* Planned Content */}
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Planned Content</h3>
            
            <div className="space-y-4">
              {/* Caption */}
              <div>
                <h4 className="text-sm font-medium text-gray-700 mb-2 flex items-center">
                  <FileText className="h-4 w-4 mr-1" />
                  Planned Caption
                </h4>
                <div className="bg-gray-50 rounded-md p-3">
                  <p className="text-sm text-gray-900 whitespace-pre-wrap">
                    {draft.plannedCaption || 'No caption provided'}
                  </p>
                </div>
              </div>

              {/* Hashtags */}
              <div>
                <h4 className="text-sm font-medium text-gray-700 mb-2 flex items-center">
                  <Hash className="h-4 w-4 mr-1" />
                  Planned Hashtags
                </h4>
                <div className="bg-gray-50 rounded-md p-3">
                  {(() => {
                    // Safely handle plannedHashtags as string or array
                    const hashtags = Array.isArray(draft.plannedHashtags) 
                      ? draft.plannedHashtags 
                      : (typeof draft.plannedHashtags === 'string' && draft.plannedHashtags.trim())
                        ? draft.plannedHashtags.split(',').map(tag => tag.trim()).filter(tag => tag)
                        : [];
                    
                    return hashtags.length > 0 ? (
                      <div className="flex flex-wrap gap-2">
                        {hashtags.map((hashtag, index) => (
                          <span
                            key={index}
                            className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800"
                          >
                            #{hashtag}
                          </span>
                        ))}
                      </div>
                    ) : (
                      <p className="text-sm text-gray-600">No hashtags specified</p>
                    );
                  })()}
                </div>
              </div>

              {/* Mentions */}
              <div>
                <h4 className="text-sm font-medium text-gray-700 mb-2 flex items-center">
                  <AtSign className="h-4 w-4 mr-1" />
                  Planned Mentions
                </h4>
                <div className="bg-gray-50 rounded-md p-3">
                  <p className="text-sm text-gray-900">
                    {draft.plannedMentions || 'No mentions specified'}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Previous Feedback (if resubmission) */}
          {draft.brandFeedback && draft.status === 'rejected' && (
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Previous Feedback</h3>
              <div className="bg-red-50 border border-red-200 rounded-md p-4">
                <p className="text-sm text-red-800">{draft.brandFeedback}</p>
                {draft.rejectedAt && (
                  <p className="text-xs text-red-600 mt-2">
                    Feedback provided on {formatDate(draft.rejectedAt)}
                  </p>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Right Sidebar */}
        <div className="xl:col-span-1 space-y-6">
          {/* Campaign Requirements */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
            <div className="flex items-center mb-4">
              <div className="w-8 h-8 bg-orange-100 rounded-lg flex items-center justify-center mr-3">
                <Hash className="h-4 w-4 text-orange-600" />
              </div>
              <h3 className="text-base font-semibold text-gray-900">Requirements</h3>
            </div>
            
            <div className="space-y-4">
              {/* Required Hashtags */}
              {(() => {
                const requiredHashtags = Array.isArray(campaign?.requiredHashtags) 
                  ? campaign.requiredHashtags 
                  : (typeof campaign?.requiredHashtags === 'string' && campaign.requiredHashtags.trim())
                    ? campaign.requiredHashtags.split(',').map(tag => tag.trim()).filter(tag => tag)
                    : [];
                
                return requiredHashtags.length > 0 ? (
                  <div>
                    <p className="text-xs font-semibold text-gray-700 uppercase tracking-wide mb-2">Required Hashtags</p>
                    <div className="space-y-2">
                      {requiredHashtags.map((hashtag, index) => {
                        const plannedHashtags = Array.isArray(draft.plannedHashtags) 
                          ? draft.plannedHashtags 
                          : (typeof draft.plannedHashtags === 'string' && draft.plannedHashtags.trim())
                            ? draft.plannedHashtags.split(',').map(tag => tag.trim()).filter(tag => tag)
                            : [];
                        
                        const isIncluded = plannedHashtags.includes(hashtag);
                        
                        return (
                          <div key={index} className={`flex items-center justify-between p-2 rounded-lg ${isIncluded ? 'bg-green-50' : 'bg-red-50'}`}>
                            <span className="text-sm font-medium text-gray-900">#{hashtag}</span>
                            {isIncluded ? (
                              <CheckCircle className="h-4 w-4 text-green-600" />
                            ) : (
                              <XCircle className="h-4 w-4 text-red-600" />
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ) : null;
              })()}

              {/* Required Mentions */}
              {campaign?.requiredMentions && (
                <div>
                  <p className="text-xs font-semibold text-gray-700 uppercase tracking-wide mb-2">Required Mentions</p>
                  <div className={`flex items-center justify-between p-2 rounded-lg ${draft.plannedMentions?.includes(campaign.requiredMentions) ? 'bg-green-50' : 'bg-red-50'}`}>
                    <span className="text-sm font-medium text-gray-900">@{campaign.requiredMentions}</span>
                    {draft.plannedMentions?.includes(campaign.requiredMentions) ? (
                      <CheckCircle className="h-4 w-4 text-green-600" />
                    ) : (
                      <XCircle className="h-4 w-4 text-red-600" />
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Submission Timeline */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
            <div className="flex items-center mb-4">
              <div className="w-8 h-8 bg-indigo-100 rounded-lg flex items-center justify-center mr-3">
                <Clock className="h-4 w-4 text-indigo-600" />
              </div>
              <h3 className="text-base font-semibold text-gray-900">Timeline</h3>
            </div>
            
            <div className="space-y-3">
              <div className="flex items-center justify-between py-2">
                <span className="text-xs font-semibold text-gray-700 uppercase tracking-wide">Submitted</span>
                <span className="text-sm text-gray-900 font-medium">{formatDate(draft.submittedAt)}</span>
              </div>
              
              {draft.resubmittedAt && (
                <div className="flex items-center justify-between py-2 border-t border-gray-100">
                  <span className="text-xs font-semibold text-blue-700 uppercase tracking-wide">Resubmitted</span>
                  <span className="text-sm text-gray-900 font-medium">{formatDate(draft.resubmittedAt)}</span>
                </div>
              )}
              
              {draft.approvedAt && (
                <div className="flex items-center justify-between py-2 border-t border-gray-100">
                  <span className="text-xs font-semibold text-green-700 uppercase tracking-wide">Approved</span>
                  <span className="text-sm text-gray-900 font-medium">{formatDate(draft.approvedAt)}</span>
                </div>
              )}
              
              {draft.rejectedAt && (
                <div className="flex items-center justify-between py-2 border-t border-gray-100">
                  <span className="text-xs font-semibold text-red-700 uppercase tracking-wide">Rejected</span>
                  <span className="text-sm text-gray-900 font-medium">{formatDate(draft.rejectedAt)}</span>
                </div>
              )}
            </div>
          </div>

          {/* Action Buttons */}
          {draft.status === 'pending' && (
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
              <div className="flex items-center mb-4">
                <div className="w-8 h-8 bg-emerald-100 rounded-lg flex items-center justify-center mr-3">
                  <CheckCircle className="h-4 w-4 text-emerald-600" />
                </div>
                <h3 className="text-base font-semibold text-gray-900">Actions</h3>
              </div>
              
              <div className="space-y-3">
                <button
                  onClick={() => setShowApprovalModal(true)}
                  disabled={submitting}
                  className="w-full flex items-center justify-center px-4 py-3 text-sm font-semibold rounded-lg text-white bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 shadow-sm hover:shadow-md"
                >
                  {submitting ? 'Processing...' : 'Approve Draft'}
                </button>
                
                <button
                  onClick={() => setShowRejectionModal(true)}
                  disabled={submitting}
                  className="w-full flex items-center justify-center px-4 py-3 text-sm font-semibold rounded-lg text-white bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 shadow-sm hover:shadow-md"
                >
                  {submitting ? 'Processing...' : 'Request Revision'}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Approval Modal */}
      {showApprovalModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900">Approve Content Draft</h3>
                <button
                  onClick={() => setShowApprovalModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <XCircle className="h-6 w-6" />
                </button>
              </div>
              
              <div className="mb-4">
                <p className="text-sm text-gray-600 mb-2">
                  Approve this content draft to allow the influencer to post their content.
                </p>
                
                <textarea
                  value={feedbackText}
                  onChange={(e) => setFeedbackText(e.target.value)}
                  placeholder="Optional feedback for the influencer..."
                  className="w-full p-3 border border-gray-300 rounded-md focus:ring-primary focus:border-primary"
                  rows={3}
                />
              </div>
              
              <div className="space-y-3">
                <button
                  onClick={() => setShowApprovalModal(false)}
                  disabled={submitting}
                  className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-500 disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleApproveDraft}
                  disabled={submitting}
                  className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 disabled:opacity-50"
                >
                  {submitting ? 'Approving...' : 'Approve Draft'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Rejection Modal */}
      {showRejectionModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900">Request Revision</h3>
                <button
                  onClick={() => setShowRejectionModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <XCircle className="h-6 w-6" />
                </button>
              </div>
              
              <div className="mb-4">
                <p className="text-sm text-gray-600 mb-2">
                  Provide specific feedback for the influencer to improve their content draft.
                </p>
                
                <textarea
                  value={feedbackText}
                  onChange={(e) => setFeedbackText(e.target.value)}
                  placeholder="Please provide specific feedback about what needs to be changed..."
                  className="w-full p-3 border border-gray-300 rounded-md focus:ring-primary focus:border-primary"
                  rows={4}
                  required
                />
              </div>
              
              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => setShowRejectionModal(false)}
                  disabled={submitting}
                  className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-500 disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleRejectDraft}
                  disabled={submitting || !feedbackText.trim()}
                  className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 disabled:opacity-50"
                >
                  {submitting ? 'Requesting...' : 'Request Revision'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ContentPreApprovalDetail;
